import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/errors/use-toast';

export interface TabSettings {
  tasks: boolean;
  documents: boolean;
  research: boolean;
  images: boolean;
  work_order: boolean;
  services?: boolean; // Keep for backwards compatibility
  development?: boolean; // Keep for backwards compatibility
}

export interface ProjectSettings {
  navigation?: {
    tabs?: TabSettings;
  };
}

// Default settings if none exist
export const DEFAULT_TAB_SETTINGS: TabSettings = {
  tasks: true,
  documents: true,
  research: true,
  images: true,
  work_order: false,
};

// Parse settings from database JSONB
export const parseProjectSettings = (settings: any): ProjectSettings => {
  if (!settings) {
    return {
      navigation: {
        tabs: DEFAULT_TAB_SETTINGS
      }
    };
  }

  // Ensure proper structure with defaults
  // Handle backwards compatibility: if development exists, use it for services
  const tabs = settings?.navigation?.tabs || {};
  const processedTabs = {
    ...DEFAULT_TAB_SETTINGS,
    ...tabs
  };
  
  // Migrate old fields to work_order if needed
  if ('services' in tabs && !('work_order' in tabs)) {
    processedTabs.work_order = tabs.services;
  } else if ('development' in tabs && !('work_order' in tabs)) {
    processedTabs.work_order = tabs.development;
  }
  
  return {
    navigation: {
      tabs: processedTabs
    }
  };
};

// Get tab settings with defaults
export const getTabSettings = (settings: any): TabSettings => {
  const parsed = parseProjectSettings(settings);
  return parsed.navigation?.tabs || DEFAULT_TAB_SETTINGS;
};

// Hook to update project settings
export const useUpdateProjectSettings = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ projectId, settings }: { projectId: string; settings: ProjectSettings }) => {
      const { error } = await supabase
        .from('workbook')
        .update({ settings })
        .eq('id', projectId);

      if (error) throw error;
      return settings;
    },
    onSuccess: (_, variables) => {
      // Invalidate project navigation query to refresh all components
      queryClient.invalidateQueries({ 
        queryKey: ['project-navigation', variables.projectId] 
      });
      
      toast({
        title: "Settings Updated",
        description: "Your navigation settings have been saved.",
        duration: 3000,
      });
    },
    onError: (error) => {
      console.error('Failed to update project settings:', error);
      toast({
        title: "Update Failed",
        description: "Failed to save your settings. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    },
  });
};